<template>
    <div v-if="hasToken">
        <button class="btn chat-fab background-default" @click="openChat">
            <i class="bi bi-chat-dots fs-4"></i>
        </button>

        <div v-if="isChatOpen" :class="['chat-screen', { 'chat-desktop': !isMobile }]">
            <div
                class="chat-header background-default text-white p-3 rounded-top-4 d-flex justify-content-between align-items-center">
                <div class="d-flex align-items-center gap-2">
                    <i class="bi bi-robot fs-5"></i>
                    <strong class="mb-0">Assistente Dailypag</strong>
                </div>
                <button class="btn btn-sm text-white border-0 p-0" @click="isChatOpen = false">
                    <i class="bi bi-x-lg fs-5"></i>
                </button>
            </div>

            <div class="chat-body" ref="chatBody">
                <div v-for="(msg, i) in messages" :key="i"
                    :class="msg.type === 'user' ? 'user-message-container' : 'bot-message-container'">
                    <!-- Procure onde exibe a mensagem do bot e ajuste para incluir o botão de ação -->
                    <div v-if="msg.type === 'bot' || msg.type === 'assistant'"
                        class="d-flex flex-column align-items-start w-100">

                        <!-- Balão de texto limpo (sem nenhuma das tags de ação) -->
                        <div class="bubble bot-bubble text-sm leading-relaxed"
                            v-html="formatMarkdown(getCleanMessageText(msg.text))"></div>

                        <!-- Botão 1: Ir para Saques -->
                        <button v-if="hasWithdrawalAction(msg.text)" @click="goToWithdrawal"
                            class="btn btn-pmg-primary btn-sm rounded-pill px-4 py-2 mt-2 shadow-sm d-flex align-items-center gap-2 fw-bold animate-fade-in">
                            <i class="bi bi-wallet2"></i> Ir para Saques
                        </button>

                        <!-- Botão 2: Criar Página com IA (Novo!) -->
                        <button v-if="hasCreatePageAction(msg.text)" @click="goToCreatePage"
                            class="btn btn-pmg-primary btn-sm rounded-pill px-4 py-2 mt-2 shadow-sm d-flex align-items-center gap-2 fw-bold animate-fade-in">
                            <i class="bi bi-magic"></i> Criar Página com IA
                        </button>
                    </div>
                    <div v-else class="bubble user-bubble">
                        {{ msg.text }}
                    </div>
                </div>

                <div v-if="isLoading && !isStreaming" class="bot-message-container">
                    <div class="bubble bot-bubble typing-indicator">
                        <span class="dot"></span>
                        <span class="dot"></span>
                        <span class="dot"></span>
                        <small class="ms-2 text-muted fst-italic">Escrevendo...</small>
                    </div>
                </div>
            </div>

            <div class="chat-input p-3 border-top bg-white rounded-bottom-4 d-flex gap-2">
                <input v-model="query" @keyup.enter="submitQuery" :disabled="isLoading"
                    class="form-control rounded-pill border-light-subtle shadow-sm px-3"
                    placeholder="Digite sua dúvida...">
                <button
                    class="btn background-default text-white rounded-circle shadow-sm d-flex align-items-center justify-content-center"
                    style="width: 45px; height: 45px; flex-shrink: 0;" @click="submitQuery"
                    :disabled="isLoading || !query">
                    <i v-if="!isLoading" class="bi bi-send-fill"></i>
                    <div v-else class="spinner-border spinner-border-sm text-white" role="status"></div>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';

export default {
    name: 'DonationFaq',
    data() {
        return {
            hasToken: false,
            isChatOpen: false,
            isLoading: false,
            isStreaming: false,
            query: '',
            isMobile: window.innerWidth <= 768,
            messages: [],
            isHistoryLoading: false
        }
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
        const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
        this.hasToken = !!token;
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            this.isMobile = window.innerWidth <= 768;
        },

        async openChat() {
            this.isChatOpen = true;
            if (this.messages.length === 0) {
                await this.loadHistory();
            }
            this.scrollToBottom();
        },

        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.chatBody;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        },

        async submitQuery() {
            if (!this.query || this.isLoading) return;

            const userText = this.query;

            this.messages.push({ type: 'user', text: userText });
            this.query = '';
            this.isLoading = true;
            this.isStreaming = false;
            this.scrollToBottom();

            const botMessageIndex = this.messages.push({ type: 'bot', text: '' }) - 1;

            try {
                // 1. Resgata o token do LocalStorage (exatamente o que o interceptor do Axios faz)
                const token = localStorage.getItem('auth_token') || localStorage.getItem('access_token');
                const baseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api';

                // 2. Dispara o fetch com o token injetado no Header (Authorization)
                const response = await fetch(`${baseUrl}/client-jwt/donation-chat/ask`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'text/event-stream',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ question: userText })
                });

                // 3. Valida se o token foi aceito pelo backend
                if (response.status === 401) {
                    this.messages[botMessageIndex].text = "Sua sessão expirou. Por favor, faça login novamente.";
                    this.isLoading = false;
                    return;
                }

                if (!response.ok) throw new Error("Server error");
                if (!response.body) throw new Error("No response body");

                // 4. Inicia a leitura do Stream de dados (letra por letra)
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let isDone = false;

                while (!isDone) {
                    const { done, value } = await reader.read();
                    isDone = done;

                    if (value) {
                        if (!this.isStreaming) {
                            this.isStreaming = true;
                        }

                        const chunk = decoder.decode(value, { stream: true });
                        const lines = chunk.split('\n');

                        lines.forEach(line => {
                            const trimmedLine = line.trim();
                            if (trimmedLine.startsWith('data: ')) {
                                try {
                                    const rawJson = trimmedLine.replace('data: ', '');
                                    const payload = JSON.parse(rawJson);

                                    if (payload.text) {
                                        this.messages[botMessageIndex].text += payload.text;
                                        this.scrollToBottom();
                                    }
                                } catch (e) {
                                    console.error("Erro ao processar JSON da IA:", e);
                                }
                            }
                        });
                    }
                }
            } catch (error) {
                this.messages[botMessageIndex].text = "Puxa, tivemos uma falha de conexão! 😅 Pode tentar enviar novamente?";
                console.error("Stream error:", error);
            } finally {
                this.isLoading = false;
                this.isStreaming = false;
                this.scrollToBottom();
            }
        },

        formatMarkdown(text) {
            if (!text) return '';

            let formattedText = text
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/^### (.*$)/gim, '<h6 class="fw-bold mt-2 mb-1" style="color: #561569;">$1</h6>')
                .replace(/\*\*(.*?)\*\*/g, '<strong class="fw-bold text-dark">$1</strong>')
                .replace(/\*(.*?)\*/g, '<em class="fst-italic">$1</em>')
                .replace(/^\* (.*$)/gim, '<li class="ms-3 mb-1">$1</li>');

            return formattedText.split('\n').join('<br>');
        },

        async loadHistory() {
            this.isHistoryLoading = true;
            try {
                // Bem mais limpo: o Axios cuida do Token e da Base URL automaticamente
                const response = await api.get('/client-jwt/donation-chat/history');

                // No Axios, o corpo da resposta fica dentro da propriedade .data
                const history = response.data.data || [];

                if (history.length > 0) {
                    this.messages = history.map(msg => ({
                        type: msg.role,
                        text: msg.content
                    }));
                } else {
                    // Saudação inicial caso o banco não retorne mensagens antigas
                    this.messages.push({
                        type: 'bot',
                        text: 'Olá! 👋 Sou o assistente da **Dailypag**. Sabia que criar sua vaquinha aqui é **100% grátis** e leva menos de 3 minutos com ajuda da nossa I.A.? Como posso te ajudar hoje? 🚀'
                    });
                }
            } catch (error) {
                console.error("Erro ao carregar histórico:", error);

                // Fallback de segurança se a rota falhar
                this.messages.push({
                    type: 'bot',
                    text: 'Olá! 👋 Sou o assistente da **Dailypag**. Como posso te ajudar hoje? 🚀'
                });
            } finally {
                this.isHistoryLoading = false;
                this.scrollToBottom();
            }
        },
        hasWithdrawalAction(text) {
            if (!text) return false;
            return text.includes('[ACTION:WITHDRAWAL]');
        },
        hasCreatePageAction(text) {
            return text && text.includes('[ACTION:CREATE_PAGE]');
        },

        getCleanMessageText(text) {
            if (!text) return '';
            return text
                .replace('[ACTION:WITHDRAWAL]', '')
                .replace('[ACTION:CREATE_PAGE]', '')
                .trim();
        },

        goToWithdrawal() {
            this.isChatOpen = false;

            if (this.$router) {
                this.$router.push('/dashboard/withdraw-list');
            } else {
                window.location.href = '/dashboard/withdraw-list';
            }
        },
        goToCreatePage() {
            this.isChatOpen = false;
            if (this.$router) {
                this.$router.push('/dashboard/new-page'); 
            } else {
                window.location.href = '/dashboard/new-page';
            }
        }
    }
}
</script>

<style scoped>
/* Container Principal */
.chat-screen {
    width: 380px;
    height: 650px;
    display: flex;
    flex-direction: column;
    background: #f8fafc;
    border-radius: 20px;
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
    position: fixed;
    bottom: 100px;
    right: 30px;
    z-index: 1000;
}

/* Área de Mensagens */
.chat-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.25rem;
    overflow-y: auto;
    background-color: #f8fafc;
}

/* Bolhas de Mensagem */
.user-message-container {
    display: flex;
    justify-content: flex-end;
}

.bot-message-container {
    display: flex;
    justify-content: flex-start;
}

.bubble {
    max-width: 85%;
    padding: 12px 16px;
    border-radius: 16px;
    font-size: 0.9rem;
    line-height: 1.5;
    word-wrap: break-word;
}

/* Estilo da Bolha do Usuário */
.user-bubble {
    background-color: #561569;
    color: white;
    border-bottom-right-radius: 4px;
    box-shadow: 0 2px 5px rgba(86, 21, 105, 0.2);
}

/* Estilo da Bolha do Bot */
.bot-bubble {
    background-color: white;
    color: #334155;
    border: 1px solid #e2e8f0;
    border-bottom-left-radius: 4px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.bot-bubble:empty {
    display: none;
}

/* Animação Digitanto */
.typing-indicator {
    display: flex;
    align-items: center;
    padding: 10px 16px !important;
}

.dot {
    width: 5px;
    height: 5px;
    margin: 0 2px;
    background: #561569;
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out both;
}

.dot:nth-child(1) {
    animation-delay: -0.32s;
}

.dot:nth-child(2) {
    animation-delay: -0.16s;
}

@keyframes bounce {

    0%,
    80%,
    100% {
        transform: scale(0);
    }

    40% {
        transform: scale(1.0);
    }
}

/* Botão Flutuante e Cores */
.background-default {
    background-color: #561569 !important;
    color: white !important;
    border-color: #561569 !important;
}

.background-default:hover {
    background-color: #3F0F4E !important;
}

.chat-fab {
    position: fixed;
    bottom: 30px;
    right: 30px;
    z-index: 999;
    width: 65px;
    height: 65px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 20px rgba(86, 21, 105, 0.3);
    cursor: pointer;
    transition: transform 0.2s ease;
}

.chat-fab:hover {
    transform: scale(1.05);
}

/* Ajuste Mobile */
@media (max-width: 768px) {
    .chat-screen {
        width: 100%;
        height: 100%;
        bottom: 0;
        right: 0;
        border-radius: 0;
    }

    .chat-header {
        border-radius: 0 !important;
    }

    .chat-input {
        border-radius: 0 !important;
        padding-bottom: env(safe-area-inset-bottom, 1rem) !important;
    }
}
</style>